import { Alert, Button, SubmitButton, Typography } from 'omakase';
import { Head } from 'head/Head';
import { LocalDateTime } from 'js-joda';
import { PurchaseDetails } from 'components/business/receipts/PurchaseDetails';
import { StoreType } from '@tock/shared/utils/connect';
import { Theme, css } from '@emotion/react';
import { core } from '@tock/coretypes';
import { hasAnyMoniesInvolved, isPurchaseOfEvent } from 'utils/purchase';
import { isThirdPartyDeliveryVariety } from '@tock/shared/utils/offerings';
import { mapPurchasedOptions } from '@tock/shared/utils/option';
import { useApp } from 'selectors/appSelector';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import ItemizedReceipt from 'components/business/receipts/ItemizedReceipt';
import LoadingComponent from '@tock/shared/components/LoadingComponent';
import React from 'react';

interface CancelBookingProps {
  business: StoreType['business'];
  isAuthenticated: boolean;
  isSubmitting: boolean;
  onCancelBooking(): void;
  onRescheduleBooking(): void;
  purchase: core.PastPurchase;
}

const title = 'Do you want to cancel your reservation?';
const subtitleFirstSentence = `You may continue cancelling the reservation, or modify it to accommodate your needs.`;
const subtitleSecondSentenceFree =
  'If you cancel now, the cancellation will be effective immediately.';
const subtitleSecondSentenceRefund =
  'If you cancel now, the cancellation will be effective immediately and you will be issued a full refund for the reservation.';

const CancelBooking: React.FunctionComponent<CancelBookingProps> = ({
  business,
  isAuthenticated,
  isSubmitting,
  onCancelBooking,
  onRescheduleBooking,
  purchase,
}) => {
  const isPQCTokenizedLinkAlertEnabled = useFeatureFlag('TOKENIZED_LINK_ACCOUNT_ALERT');
  const { patron } = useApp();

  if (!business.business) {
    return <LoadingComponent isLoading={true} isBallPulse={true} />;
  }

  const hasMoneyInvolved = hasAnyMoniesInvolved(purchase);
  const subtitle = [
    subtitleFirstSentence,
    hasMoneyInvolved ? subtitleSecondSentenceRefund : subtitleSecondSentenceFree,
  ].join('\n');

  // TODO, encapsulate this into a wrapper for itemized receipt or do it inside of it?
  const options = mapPurchasedOptions(purchase);

  const businessName = business.business.name || '';

  const showModify =
    purchase.canReschedule && !purchase.cancelledOrRefunded && !business.business.bookingsDisabled;

  const purchaseToken = new URLSearchParams(window.location.search).get('token');
  const displayTokenizedLinkLoggedInUserMessage =
    purchaseToken &&
    isAuthenticated &&
    isPQCTokenizedLinkAlertEnabled &&
    patron?.email !== purchase.ownerPatron?.email;

  return (
    <div
      css={css`
        align-items: center;
        display: flex;
        flex-direction: column;
        justify-content: center;
        width: 100%;
      `}
    >
      <div
        css={({ breakpoints, spacing }: Theme) => css`
          margin-top: ${spacing(3)}px;

          ${breakpoints.up('sm')} {
            margin-top: ${spacing(6)}px;
            max-width: 64rem;
          }
        `}
      >
        <Head title={`${businessName} - Cancel`} />
        <Typography
          css={({ breakpoints }: Theme) => css`
            display: none;

            ${breakpoints.up('sm')} {
              display: block;
            }
          `}
          component="h1"
          variant="heading02"
          color="fgDefault"
          mb={1.5}
        >
          {title}
        </Typography>
        <Typography
          component="h2"
          variant="body02"
          color="fgDefault"
          css={({ breakpoints, spacing }: Theme) => css`
            margin-bottom: ${spacing(3)}px;

            ${breakpoints.up('sm')} {
              margin-bottom: ${spacing(5)}px;
            }
          `}
        >
          {subtitle}
        </Typography>
        <div
          css={({ breakpoints, spacing }: Theme) => css`
            margin-bottom: ${spacing(3)}px;

            ${breakpoints.up('sm')} {
              margin-bottom: ${spacing(5)}px;
            }
          `}
        >
          <PurchaseDetails
            business={business.business}
            dateTime={LocalDateTime.parse(purchase.ticketDateTime)}
            diner={purchase.dinerPatron[0] as core.Patron}
            isAuthenticated={isAuthenticated}
            isEvent={isPurchaseOfEvent(purchase)}
            owner={purchase.ownerPatron as core.Patron}
            partySize={purchase.ticketCount}
            ticketTypeName={purchase.ticketTypeName}
            ticketType={purchase.ticketType as core.TicketType}
            seatingAreaName={purchase.seatingAreaName}
          >
            {hasMoneyInvolved && (
              <ItemizedReceipt
                business={business.business}
                compAmount={purchase.compAmountCents}
                customCharge={purchase.purchasedCustomCharge as core.PurchasedCustomCharge[]}
                deliveryServiceProvider={purchase.ticketType?.deliveryServiceProvider}
                extraChargeDetails={
                  purchase?.ticketType?.extraChargeDetails as core.ExtraChargeDetails
                }
                options={options}
                purchase={purchase}
                partySize={purchase.ticketCount}
                purchaseFees={purchase.fee}
                title="Receipt"
                showTockFeeIfRequired={true}
                isVisitEdit={purchase.originatedAt === 'VISIT_EDIT'}
                isGratuityForThirdPartyDelivery={isThirdPartyDeliveryVariety(purchase.ticketType)}
                potentialRefund={purchase.netAmountPaidCents}
                ticketType={purchase.ticketType || undefined}
              />
            )}
          </PurchaseDetails>
        </div>
        {displayTokenizedLinkLoggedInUserMessage && (
          <div
            css={({ spacing }: Theme) => css`
              margin-bottom: ${spacing(5)}px;
            `}
          >
            <Alert severity="info">
              <Typography mb={1}>
                To modify or cancel your reservation, please make sure you're using the same email
                associated with the booked reservation.
              </Typography>
            </Alert>
          </div>
        )}

        <div
          css={({ breakpoints, spacing }: Theme) => css`
            margin-bottom: ${spacing(5)}px;

            ${breakpoints.up('sm')} {
              display: flex;

              & > * + * {
                margin-left: ${spacing(1)}px;
              }
            }
          `}
        >
          {showModify && (
            <Button
              fullWidth
              variant="basic"
              disabled={isSubmitting}
              onClick={onRescheduleBooking}
              data-testid="modify-reservation-button"
              size="large"
              css={({ breakpoints, spacing }: Theme) => css`
                margin-bottom: ${spacing(2)}px;

                ${breakpoints.up('sm')} {
                  margin-bottom: 0;
                }
              `}
            >
              Modify reservation
            </Button>
          )}

          <SubmitButton
            fullWidth
            variant="primary"
            disabled={isSubmitting}
            isSubmitting={isSubmitting}
            onClick={onCancelBooking}
            data-testid="cancel-reservation-button"
            size="large"
          >
            Cancel reservation
          </SubmitButton>
        </div>
      </div>
    </div>
  );
};

export default CancelBooking;
